import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useOutletContext, useLocation } from 'react-router-dom';
import moment from 'moment';
import WebButton from '../../../../component/library/webButton';
import { getPriceForQuantity } from '../../../component/views/productDetails/utils';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import { PRICE_LOGIC, SellingModes } from '../../../../helper/constants';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import '../../productDetails/style.css';

// Helper to check if dropshipping is available for a product
const isDropshippingAvailableFromTags = (tagInfoList = []) => {
  const isOnePsaleTag = tagInfoList.find(tag => tag.key === 'isOnePsale');
  // Dropshipping is available if isOnePsale is not false
  return !(isOnePsaleTag?.value === false || isOnePsaleTag?.value === 'false');
};

const getEffectiveWholesaleMOQ = (cartItem = {}) => {
  const parseQty = value => {
    const qty = Number(value);
    return Number.isFinite(qty) && qty > 0 ? qty : 0;
  };

  const product = cartItem?.product || {};
  const isProductDropshippingAvailable = isDropshippingAvailableFromTags(product?.tagInfoList || []);
  
  // One-piece not available: actual product wholesale MOQ only (no min-2 from tiers)
  if (!isProductDropshippingAvailable) {
    const actualMOQ = [
      parseQty(product?.moq),
      parseQty(product?.minOrderQuantity),
      parseQty(product?.min_order_quantity),
      parseQty(product?.productSaleInfo?.minOrderQuantity),
    ].filter(Boolean);
    return actualMOQ.length ? Math.max(...actualMOQ) : 0;
  }

  const firstRangeMOQ = parseQty(
    cartItem?.priceRanges?.[0]?.startQuantity ??
    cartItem?.priceRanges?.[0]?.start_quantity ??
    product?.priceRanges?.[0]?.startQuantity ??
    product?.priceRanges?.[0]?.start_quantity
  );

  const candidates = [
    parseQty(product?.quantity),
    parseQty(product?.moq),
    parseQty(product?.minOrderQuantity),
    parseQty(product?.min_order_quantity),
    parseQty(product?.productSaleInfo?.minOrderQuantity),
    firstRangeMOQ,
  ].filter(Boolean);

  let minMOQ = candidates.length ? Math.max(...candidates) : 0;
  if (minMOQ === 1 && isProductDropshippingAvailable) minMOQ = 2;
  return minMOQ;
};

export function CheckOutBox({ type, proceedToPayment, cart, allCartItems, selectedItems = new Set(), isAllLoadingCard, hasAllProductCardsMinMOQ = true, pendingSaveVariants = new Set(), cartTotalFinalPrice = 0, cartLMD = 0, cartTotalLMD = 0, selectedPaymentMethods = {}, imPoints = null, imCheckoutPreview = null, isImPointsExpired = false }) {
  const { setShowMaintenancePopup, totalLMDCharges, gstOnLMD, totalLMDWithGST } = useOutletContext() || {};

  // Calculate final total for payment page based on selected payment method
  const getPaymentAdjustedTotal = useMemo(() => {
    const empty = {
      originalTotal: null,
      imPointsDiscount: 0,
      imCashDiscount: 0,
      totalDiscount: 0,
      finalTotal: null,
      hasIMPointsDiscount: false,
      hasIMCashDiscount: false,
      selectedMethods: []
    };

    if (type !== 'payment' || !selectedPaymentMethods) return empty;

    const selectedMethods = Object.keys(selectedPaymentMethods).filter(m => selectedPaymentMethods[m]);

    // No method selected — return empty so Grand Total shows the raw calculated price
    if (selectedMethods.length === 0) return empty;

    const imPointsSelected = selectedMethods.includes('im_points');
    const walletSelected = selectedMethods.includes('wallet');

    const imPointsDiscount = (imPointsSelected && imCheckoutPreview && !isImPointsExpired)
      ? (imCheckoutPreview.imPointsDiscount || 0)
      : 0;

    const imCashDiscount = (walletSelected && imCheckoutPreview)
      ? (imCheckoutPreview.imCashDiscount || 0)
      : 0;

    const totalDiscount = imPointsDiscount + imCashDiscount;

    // finalTotal should only override the display when IM discounts are actually applied.
    // For non-IM methods (bank_transfer, ccavenue, razorpay), leave finalTotal as null so
    // the Grand Total continues to use the same raw calculation path as when nothing is selected.
    const finalTotal = totalDiscount > 0 && imCheckoutPreview
      ? (imCheckoutPreview.payableAfterIm ?? imCheckoutPreview.grossBeforeIm ?? null)
      : null;

    return {
      originalTotal: imCheckoutPreview?.grossBeforeIm || null,
      imPointsDiscount,
      imCashDiscount,
      totalDiscount,
      finalTotal,
      hasIMPointsDiscount: imPointsDiscount > 0,
      hasIMCashDiscount: imCashDiscount > 0,
      selectedMethods,
    };
  }, [type, selectedPaymentMethods, imCheckoutPreview, isImPointsExpired]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { priceSummary } = useOutletContext() || {};
  const location = useLocation();
  const navSummary = (location && location.state && location.state.priceSummaryTotals) || null;
  const navEntries = (location && location.state && location.state.priceSummaryEntries) || {};
  const date = new Date();
  const authData = getAuthLocalStorage();
  const isLoggedIn = authData.isLoggedIn && authData.loginHeader;
  const authToken = authData.loginHeader;
  const [showDutiesDetails, setShowDutiesDetails] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false); // State for mobile products dropdown
  const totalOrderAmountRef = useRef(0);

  // State to store wholesale pricing breakdown for each product group
  const [wholesalePricingData, setWholesalePricingData] = useState(new Map());
  const [loadingWholesalePricing, setLoadingWholesalePricing] = useState(false);
  const abortControllersRef = useRef(new Map());

  // Get current cart item IDs for filtering stale entries
  const currentCartItemIds = useMemo(() => {
    return new Set(cart?.map(item => item._id) || []);
  }, [cart]);

  // Filter entries to only include current cart items (handles case where card removed but cleanup pending)
  const filteredEntries = useMemo(() => {
    if (!priceSummary?.entries) return {};
    const entries = priceSummary.entries;
    const filtered = {};
    for (const [key, value] of Object.entries(entries)) {
      if (currentCartItemIds.has(key)) {
        filtered[key] = value;
      }
    }
    return filtered;
  }, [priceSummary?.entries, currentCartItemIds]);

  // Recalculate totals from filtered entries
  const filteredSummary = useMemo(() => {
    if (Object.keys(filteredEntries).length === 0) return null;
    const combined = Object.values(filteredEntries).reduce(
      (acc, e) => {
        if (e && typeof e === 'object') {
          const getNum = val => {
            const num = parseFloat(val);
            return isNaN(num) ? 0 : num;
          };
          acc.LS += getNum(e.LS);
          acc.CD += getNum(e.CD);
          acc.IF += getNum(e.IF);
          acc.LMD += getNum(e.LMD);
          acc.PF += getNum(e.PF);
          acc.GST_PR += getNum(e.GST_PR);
          acc.GST_FR += getNum(e.GST_FR);
          acc.GST_PF += getNum(e.GST_PF);
          acc.TOTAL_PRICE += getNum(e.TOTAL_PRICE);
        }
        return acc;
      },
      { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, TOTAL_PRICE: 0 }
    );
    return { ...combined, entries: filteredEntries };
  }, [filteredEntries]);

  // Combine duties & taxes fields from priceSummary (single combined totals provided by parent)
  const summarySource = useMemo(() => {
    // On address/payment pages, prioritize navSummary (from navigation state)
    if (type !== 'cart' && navSummary) {
      return navSummary;
    }

    // Use filtered summary if available (handles removal transition)
    if (filteredSummary && Object.keys(filteredEntries).length === cart.length) {
      return filteredSummary;
    }

    // Original logic: check if entries count matches cart length
    if (priceSummary?.entries && Object.keys(priceSummary.entries).length !== cart.length) return {};

    // On cart page, use priceSummary from context
    if (priceSummary && Object.keys(priceSummary).length > 0) {
      // If it has entries, it means data is being calculated
      if (priceSummary.entries && Object.keys(priceSummary.entries).length > 0) {
        return priceSummary;
      }
      // If it has any top-level totals, use it
      if ('TOTAL_PRICE' in priceSummary || 'LS' in priceSummary || 'CD' in priceSummary) {
        return priceSummary;
      }
    }

    // Fallback to navigation state if available
    if (navSummary) return navSummary;
    return {};
  }, [type, priceSummary, navSummary, cart.length, filteredSummary, filteredEntries]);

  const entriesSource = useMemo(() => {
    // On cart page, use live entries from context. On address/payment, use navigation state
    if (type === 'cart') {
      // Use filtered entries if count matches (handles removal transition)
      if (Object.keys(filteredEntries).length === cart.length) {
        return filteredEntries;
      }
      const liveEntries = priceSummary?.entries;
      if (liveEntries && Object.keys(liveEntries).length === cart.length) return liveEntries;
      return navEntries || {};
    } else {
      // For address and payment pages, prioritize navEntries
      if (navEntries && Object.keys(navEntries).length > 0) return navEntries;
      return {};
    }
  }, [type, priceSummary?.entries, navEntries, cart.length, filteredEntries]);
  const dutiesBreakdown = useMemo(() => {
    // On address/payment pages, prioritize navSummary. On cart, use summarySource
    let source = {};
    if (type !== 'cart' && navSummary) {
      // For address/payment pages, use navSummary first
      source = navSummary;
    } else {
      // For cart page, use summarySource
      source = summarySource || {};
    }

    // If source doesn't have totals (or they're all 0), calculate from entries
    const hasBreakdownInSource = source.LS !== undefined || source.CD !== undefined || source.IF !== undefined;
    if (!hasBreakdownInSource && entriesSource && Object.keys(entriesSource).length > 0) {
      source = Object.values(entriesSource).reduce(
        (acc, entry) => {
          if (entry && typeof entry === 'object') {
            acc.LS += parseFloat(entry?.LS || 0);
            acc.CD += parseFloat(entry?.CD || 0);
            acc.IF += parseFloat(entry?.IF || 0);
            acc.LMD += parseFloat(entry?.LMD || 0);
            acc.PF += parseFloat(entry?.PF || 0);
            acc.GST_PR += parseFloat(entry?.GST_PR || 0);
            acc.GST_FR += parseFloat(entry?.GST_FR || 0);
            acc.GST_PF += parseFloat(entry?.GST_PF || 0);
            acc.TOTAL_PRICE += parseFloat(entry?.TOTAL_PRICE || 0);
          }
          return acc;
        },
        { LS: 0, CD: 0, IF: 0, LMD: 0, PF: 0, GST_PR: 0, GST_FR: 0, GST_PF: 0, TOTAL_PRICE: 0 }
      );
    }

    const LS = parseFloat(source?.LS || 0);
    const CD = parseFloat(source?.CD || 0);
    const IF = parseFloat(source?.IF || 0);
    const LMD = parseFloat(source?.LMD || 0);
    const PF = parseFloat(source?.PF || 0);
    const GST_PR = parseFloat(source?.GST_PR || 0);
    const GST_FR = parseFloat(source?.GST_FR || 0);
    const GST_PF = parseFloat(source?.GST_PF || 0);
    const totalGST = GST_PR + GST_FR + GST_PF;
    const totalDuties = LS + CD + IF + LMD + PF + totalGST;
    return { LS, CD, IF, LMD, PF, GST_PR, GST_FR, GST_PF, totalGST, totalDuties };
  }, [type, navSummary, summarySource, entriesSource]);

  // Determine calculating state based on card entries reporting
  const isCartCalculating = useMemo(() => {
    // Only show "Calculating..." while on cart page where ards are actively computing
    if (type !== 'cart') return false;
    const expected = Array.isArray(cart) ? cart.length : 0;
    // Use filtered entries count (handles removal transition where entries > cart.length)
    const received = Object.keys(filteredEntries).length;
    return received < expected;
  }, [filteredEntries, cart, type]);

  const summaryReady = useMemo(() => {
    // On cart page, if we have cartTotalFinalPrice from API, we're ready
    if (type === 'cart' && cartTotalFinalPrice > 0) return true;

    // On address/payment pages, if we have cartTotalFinalPrice from API, we're ready
    if (type !== 'cart' && cartTotalFinalPrice > 0) return true;

    if (!summarySource || Object.keys(summarySource).length === 0) return false;

    // Check for combined totals at top level - if any duty field exists, we're ready
    const hasAnyDutyField =
      'LS' in summarySource ||
      'CD' in summarySource ||
      'IF' in summarySource ||
      'LMD' in summarySource ||
      'PF' in summarySource ||
      'GST_PR' in summarySource ||
      'GST_FR' in summarySource ||
      'GST_PF' in summarySource;

    // Check if TOTAL_PRICE exists
    const hasTotalPrice = 'TOTAL_PRICE' in summarySource;

    // Check if we have entries with valid data
    const hasEntries = entriesSource && Object.keys(entriesSource).length > 0;
    const hasValidEntry = hasEntries && Object.values(entriesSource).some(entry => entry && (entry.TOTAL_PRICE !== undefined || entry.LS !== undefined));

    // If we have any duty fields, total price, or valid entries, we're ready
    return hasAnyDutyField || hasTotalPrice || hasValidEntry;
  }, [summarySource, entriesSource, cartTotalFinalPrice, type]);

  // Show calculating if any card is loading OR if summary is not ready
  const showTotalsCalculating = isAllLoadingCard?.all || !summaryReady;

  // Handle proceedToPayment - pass totals in navigation state
  const handleProceedToPayment = () => {
    // Stop checkout — maintenance mode
    // setShowMaintenancePopup?.(true);
    // return;

    if (proceedToPayment) {
      // Ensure breakdown totals are included
      const totalsToSend = {
        ...summarySource,
        // Explicitly include breakdown fields from dutiesBreakdown if not in summarySource
        LS: summarySource?.LS ?? dutiesBreakdown.LS,
        CD: summarySource?.CD ?? dutiesBreakdown.CD,
        IF: summarySource?.IF ?? dutiesBreakdown.IF,
        LMD: summarySource?.LMD ?? dutiesBreakdown.LMD,
        PF: summarySource?.PF ?? dutiesBreakdown.PF,
        GST_PR: summarySource?.GST_PR ?? dutiesBreakdown.GST_PR,
        GST_FR: summarySource?.GST_FR ?? dutiesBreakdown.GST_FR,
        GST_PF: summarySource?.GST_PF ?? dutiesBreakdown.GST_PF,
        TOTAL_PRICE: summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE,
      };
      const summaryPayload = {
        priceSummaryTotals: totalsToSend,
        priceSummaryEntries: entriesSource,
      };
      proceedToPayment(summaryPayload);
    }
  };

  const message = useMessage();
  const invalidMOQItem = useMemo(() => {
    if (!Array.isArray(cart) || cart.length === 0) return null;
    for (const cartItem of cart) {
      if (!cartItem?.product || !Array.isArray(cartItem?.product?.variants) || cartItem.product.variants.length === 0) continue;
      const pricingMode = String(cartItem?.pricingMode || '').toLowerCase();
      const isWholesaleMode = pricingMode === String(SellingModes.Wholesale || 'wholesale').toLowerCase();
      if (!isWholesaleMode) continue;

      const minMOQ = getEffectiveWholesaleMOQ(cartItem);
      const totalQuantity = cartItem.product.variants.reduce((sum, v) => sum + (Number(v?.quantity) || 0), 0);
      if (minMOQ > 0 && totalQuantity < minMOQ) {
        return {
          title: cartItem.product.title || cartItem.product.name || 'Product',
          minMOQ,
          totalQuantity,
        };
      }
    }
    return null;
  }, [cart]);

  // Handle place order - navigate to address with totals in state
  const handlePlaceOrder = () => {
    // Stop checkout — maintenance mode
    // setShowMaintenancePopup?.(true);
    // return;

    // Check if any quantities are being updated
    if (pendingSaveVariants && pendingSaveVariants.size > 0) {
      message.showToast('Please wait for quantity updates to complete before placing order.', ToastTypes.WARNING);
      return;
    }

    // Validate MOQ for all selected cart items before proceeding
    const invalidItems = [];
    cart.forEach(cartItem => {
      if (!cartItem?.product || !Array.isArray(cartItem?.product?.variants) || cartItem.product.variants.length === 0) return;
      const pricingMode = String(cartItem?.pricingMode || '').toLowerCase();
      const isWholesaleMode = pricingMode === String(SellingModes.Wholesale || 'wholesale').toLowerCase();
      if (!isWholesaleMode) return;

      const minMOQ = getEffectiveWholesaleMOQ(cartItem);
      const totalQuantity = cartItem.product.variants.reduce((sum, v) => sum + (Number(v?.quantity) || 0), 0);
      if (minMOQ > 0 && totalQuantity < minMOQ) {
        invalidItems.push({
          title: cartItem.product.title || cartItem.product.name || 'Product',
          minMOQ,
          totalQuantity,
        });
      }
    });

    if (invalidItems.length > 0) {
      const firstItem = invalidItems[0];
      message.showToast(
        `${firstItem.title}: Minimum order quantity is ${firstItem.minMOQ}. Current quantity is ${firstItem.totalQuantity}.`,
        ToastTypes.ERROR
      );
      return;
    }

    const productParam = searchParams.get('product');
    const entriesToSend = entriesSource || {};

    // Calculate TOTAL_PRICE from entries if not available in summarySource
    let totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
    if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
      // Sum up all entries' TOTAL_PRICE
      totalPrice = Object.values(entriesToSend).reduce((sum, entry) => {
        return sum + parseFloat(entry?.TOTAL_PRICE || 0);
      }, 0);
    }

    if (totalOrderAmountRef.current < PRICE_LOGIC.MIN_ORDER_PLACE_AMOUNT) return;

    const summaryToSend = {
      LS: dutiesBreakdown.LS,
      CD: dutiesBreakdown.CD,
      IF: dutiesBreakdown.IF,
      LMD: dutiesBreakdown.LMD,
      PF: dutiesBreakdown.PF,
      GST_PR: dutiesBreakdown.GST_PR,
      GST_FR: dutiesBreakdown.GST_FR,
      GST_PF: dutiesBreakdown.GST_PF,
      TOTAL_PRICE: parseFloat(totalPrice || 0),
      // Add new calculation values for Order Summary
      cartTotalFinalPrice: cartTotalFinalPrice || 0,
      cartTotalLMD: cartTotalLMD || 0,
      cartLMD: cartLMD || 0,
      calculatedTotal: (cartTotalFinalPrice || 0) + (cartTotalLMD || 0),
      cartDiscount: (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0,
    };
    navigate(`/checkout/address${productParam ? `?product=${productParam}` : ''}`, {
      state: {
        priceSummaryTotals: summaryToSend,
        priceSummaryEntries: entriesToSend,
      },
    });
  };

  const isAmountPresent = useMemo(() => {
    // On cart page, check cartTotalFinalPrice first
    if (type === 'cart' && cartTotalFinalPrice > 0) return true;

    // Check if we have valid totals from summarySource
    if (summarySource && summarySource.TOTAL_PRICE !== undefined && summarySource.TOTAL_PRICE !== null) {
      return summarySource.TOTAL_PRICE > 0;
    }
    // Check navigation state
    if (navSummary && navSummary.TOTAL_PRICE !== undefined && navSummary.TOTAL_PRICE !== null) {
      return navSummary.TOTAL_PRICE > 0;
    }
    // Check if we have entries with valid totals
    if (entriesSource && Object.keys(entriesSource).length > 0) {
      const hasValidEntry = Object.values(entriesSource).some(entry => entry && entry.TOTAL_PRICE !== undefined && entry.TOTAL_PRICE !== null && entry.TOTAL_PRICE > 0);
      if (hasValidEntry) return true;
    }
    return false;
  }, [summarySource, navSummary, entriesSource, cartTotalFinalPrice, type]);

  const isAmountZero = useMemo(() => {
    // On cart page, check cartTotalFinalPrice first
    if (type === 'cart' && cartTotalFinalPrice > 0) return false;

    // On address/payment pages, check if we have valid calculation values
    if (type !== 'cart') {
      // Check if we have the new calculation values
      if (cartTotalFinalPrice > 0 && cartLMD >= 0) return false;

      // Fallback: check navSummary
      if (navSummary) {
        if (navSummary.TOTAL_PRICE === 0) return true;
        if (navSummary.TOTAL_PRICE > 0) return false;
      }

      // On address/payment pages, if we have cartTotalFinalPrice but no summarySource, don't hide totals
      if (cartTotalFinalPrice > 0) return false;
    }

    // On cart page or if navSummary not available, check summarySource
    if ((summarySource && summarySource.TOTAL_PRICE === 0) || Object.keys(summarySource).length === 0) return true;
    return false;
  }, [type, summarySource, navSummary, cartTotalFinalPrice, cartLMD]);

  return (
    <div
      className={`flex flex-col bg-white rounded-lg overflow-hidden ${type === 'payment' || type === 'address' ? '' : 'lg:sticky lg:top-[12rem]'} ${type === 'cart' ? 'fixed bottom-0 left-0 right-0 z-10 lg:relative rounded-t-lg lg:rounded-lg' : ''} payment-options-wrapper`}
    >
      <div className="pb-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Order Summary</h3>
          {/* Mobile-only products dropdown toggle button */}
          {(type === 'address' || type === 'payment') && cart?.length > 0 && (
            <button
              type="button"
              className="lg:hidden text-[#3DD0E5] hover:text-[#33BCCF] p-1"
              onClick={() => setShowProductsDropdown(prev => !prev)}
              aria-expanded={showProductsDropdown}
              aria-label={showProductsDropdown ? 'Hide products' : 'Show products'}
            >
              {showProductsDropdown ? '▲' : '▼'}
            </button>
          )}
        </div>
      </div>

      {/* Mobile dropdown section - hidden on desktop */}
      {(type === 'address' || type === 'payment') && cart?.length > 0 && showProductsDropdown && (
        <div className="lg:hidden px-6 mb-4 max-h-[400px] overflow-y-auto custom-scrollbar border-t border-gray-100 pt-4">
          <div className="space-y-2">
            {cart.flatMap((cartItem, cartIndex) => {
              const { product } = cartItem;
              const isWholesale = cartItem.pricingMode === 'wholesale';
              return (product?.variants || [])
                .filter(variant => variant.quantity > 0)
                .map((variant, variantIndex) => {
                  const attrs = variant?.skuAttributes || [];
                  const attrText = attrs
                    .map(a => a.value || a.skuValueName)
                    .filter(Boolean)
                    .join(' / ');
                  return (
                    <div key={`${cartIndex}-${variantIndex}`} className="p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <img
                          src={attrs?.[0]?.skuImageUrl || variant?.image || product?.imgUrl?.[0]}
                          className="w-10 h-10 object-cover rounded"
                          alt={product?.title || product?.name}
                          referrerPolicy="no-referrer"
                        />
                        <div className="flex-1 min-w-0">
                          {attrText && <p className="text-xs text-gray-800 font-medium truncate">{attrText}</p>}
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-500">
                              {moment(date)
                                // Temporarily using a fixed 22-day delivery estimate due to the current global war situation.
                                // TODO: Revert to product-specific handling days once shipping timelines normalize.
                                // .add(product?.handling_days || 15, 'days')
                                .add(22, 'days')
                                .format('Do MMM')}
                            </p>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${isWholesale ? 'bg-purple-100 text-purple-600' : 'bg-orange-100 text-orange-600'}`}>
                              {isWholesale ? 'Wholesale' : 'Dropship'}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-700">x{variant.quantity}</span>
                      </div>
                    </div>
                  );
                });
            })}
          </div>
        </div>
      )}

      {/* Desktop product list - always visible on desktop, hidden on mobile */}
      {(type === 'address' || type === 'payment') && cart?.length > 0 && (
        <div className="hidden lg:block px-4 mb-3 max-h-[180px] overflow-y-auto custom-scrollbar">
          <div className="space-y-2">
            {cart.flatMap((cartItem, cartIndex) => {
              const { product } = cartItem;
              const isWholesale = cartItem.pricingMode === 'wholesale';
              return (product?.variants || [])
                .filter(variant => variant.quantity > 0)
                .map((variant, variantIndex) => {
                  const attrs = variant?.skuAttributes || [];
                  const attrText = attrs
                    .map(a => a.valueTrans || a.skuValueName)
                    .filter(Boolean)
                    .join(' / ');
                  return (
                    <div key={`${cartIndex}-${variantIndex}`} className="p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <img
                          src={attrs?.[0]?.skuImageUrl || variant?.image || product?.imgUrl?.[0]}
                          className="w-10 h-10 object-cover rounded"
                          alt={product?.title || product?.name}
                          referrerPolicy="no-referrer"
                        />
                        <div className="flex-1 min-w-0">
                          {attrText && <p className="text-xs text-gray-800 font-medium truncate">{attrText}</p>}
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-gray-500">
                              {moment(date)
                                // Temporarily using a fixed 22-day delivery estimate due to the current global war situation.
                                // TODO: Revert to product-specific handling days once shipping timelines normalize.
                                // .add(product?.handling_days || 15, 'days')
                                .add(22, 'days')
                                .format('Do MMM')}
                            </p>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${isWholesale ? 'bg-purple-100 text-purple-600' : 'bg-orange-100 text-orange-600'}`}>
                              {isWholesale ? 'Wholesale' : '1-Piece'}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-700">x{variant.quantity}</span>
                      </div>
                    </div>
                  );
                });
            })}
          </div>
        </div>
      )}
      {cart.length > 0 &&
        (() => {
          let totalPrice;
          // On cart page, use cartTotalFinalPrice from API if available
          if (type === 'cart' && cartTotalFinalPrice > 0) {
            // Grand Total = cartTotalFinalPrice minus shared-shipping discount
            const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
            totalPrice = cartTotalFinalPrice - discount;
          } else if (type !== 'cart') {
            // For address/payment, use navSummary first (from navigation state)
            totalPrice = navSummary?.TOTAL_PRICE;
            // If not available, calculate from navEntries (entriesSource should be navEntries on these pages)
            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                return sum + parseFloat(entry?.TOTAL_PRICE || 0);
              }, 0);
            }
            // Final fallback to summarySource
            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
              totalPrice = summarySource?.TOTAL_PRICE;
            }
          } else {
            // For cart page, use summarySource first (live updates)
            totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
            // If not available, calculate from entries
            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                return sum + parseFloat(entry?.TOTAL_PRICE || 0);
              }, 0);
            }
          }

          totalOrderAmountRef.current = totalPrice;
          return totalPrice ? Math.round(totalPrice) < PRICE_LOGIC.MIN_ORDER_PLACE_AMOUNT : null;
        })() && (
          <div className="px-6 text-[11px] text-red-500 leading-none mb-0.5">
            Your cart total is too low. Add items worth ₹{PRICE_LOGIC.MIN_ORDER_PLACE_AMOUNT} or more to place your order.
          </div>
        )}
      <div className="pb-3 bg-white">
        <div className="space-y-2">
          {/* Mobile view: Combined row for Grand Total and Button */}
          <div className="lg:hidden">
            {!isAmountZero && (
              <div className="flex items-center justify-between border-t pt-3 mt-2">
                <div className="flex-1">
                  {/* Total = cartTotalFinalPrice (already includes per-product LMD) */}
                  <div className="flex justify-between font-bold text-base mb-2">
                    <span>Total</span>
                    <span className="text-[#3DD0E5]">
                      {showTotalsCalculating
                        ? 'Calculating...'
                        : `₹${Math.round(cartTotalFinalPrice || 0)}`}
                    </span>
                  </div>

                  {/* Shipping Discount — shown when multiple products share LMD */}
                  {(cartTotalLMD || 0) > (cartLMD || 0) && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-600">Shipping Discount</span>
                      <span className="text-green-600">
                        -₹{Math.round((cartTotalLMD || 0) - (cartLMD || 0))}
                      </span>
                    </div>
                  )}

                  <div className="font-bold text-lg flex items-center">
                    Grand Total
                    <span className="text-[#3DD0E5] ml-2">
                      {showTotalsCalculating
                        ? 'Calculating...'
                        : (() => {
                          if (getPaymentAdjustedTotal.finalTotal !== null) {
                            return `₹${Math.round(getPaymentAdjustedTotal.finalTotal)}`;
                          }

                          let totalPrice;
                          if (type === 'cart' && cartTotalFinalPrice > 0) {
                            // Grand Total = cartTotalFinalPrice minus the shared-shipping discount
                            const discount = (cartTotalLMD || 0) > (cartLMD || 0)
                              ? (cartTotalLMD || 0) - (cartLMD || 0)
                              : 0;
                            totalPrice = cartTotalFinalPrice - discount;
                          } else if (type !== 'cart') {
                            if (cartTotalFinalPrice > 0) {
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0)
                                ? (cartTotalLMD || 0) - (cartLMD || 0)
                                : 0;
                              totalPrice = cartTotalFinalPrice - discount;
                            } else {
                              totalPrice = navSummary?.TOTAL_PRICE;
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                              if (totalPrice) totalPrice -= discount;
                            }
                            if (!totalPrice) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => sum + parseFloat(entry?.TOTAL_PRICE || 0), 0);
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                              if (totalPrice) totalPrice -= discount;
                            }
                            if (!totalPrice) {
                               totalPrice = summarySource?.TOTAL_PRICE;
                               const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                               if (totalPrice) totalPrice -= discount;
                            }
                          } else {
                            totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
                            const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                            if (totalPrice) totalPrice -= discount;
                            if (!totalPrice) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => sum + parseFloat(entry?.TOTAL_PRICE || 0), 0);
                              if (totalPrice) totalPrice -= discount;
                            }
                          }
                          totalOrderAmountRef.current = totalPrice;
                          return `₹${Math.round(totalPrice || 0)}`;
                        })()}
                    </span>
                  </div>
                  <div className="text-sm font-medium mt-1">(GST and Duties included)</div>

                  {/* IM Points Applied Amount */}
                  {getPaymentAdjustedTotal.hasIMPointsDiscount && (
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t">
                      <span className="text-green-600 font-medium">IM Points Applied:</span>
                      <span className="text-green-600 font-semibold">
                        -₹{Math.round(getPaymentAdjustedTotal.imPointsDiscount)}
                      </span>
                    </div>
                  )}

                  {/* IM Cash Applied Amount */}
                  {getPaymentAdjustedTotal.hasIMCashDiscount && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-blue-600 font-medium">IM Cash Applied:</span>
                      <span className="text-blue-600 font-semibold">
                        -₹{Math.round(getPaymentAdjustedTotal.imCashDiscount)}
                      </span>
                    </div>
                  )}

                  {/* Shipping Charges for Mobile */}
                  {type !== 'cart' && totalLMDWithGST > 0 && (
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t">
                      <span className="font-medium">Shipping:</span>
                      <span className="text-[#3DD0E5] font-semibold">₹{Math.round(totalLMDWithGST)}</span>
                    </div>
                  )}

                  {/* Removed Final Total Row to avoid double counting shipping */}
                </div>

                {type !== 'payment' && (
                  <div className="ml-4">
                    <WebButton
                      className={`bg-[#3DD0E5] text-white py-2 px-4 rounded-lg hover:bg-[#33BCCF] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap`}
                      text={type === 'address' ? 'Continue' : 'Place Order'}
                      onClick={type === 'address' ? handleProceedToPayment : handlePlaceOrder}
                      type="button"
                      disabled={
                        !hasAllProductCardsMinMOQ ||
                        !!invalidMOQItem ||
                        (pendingSaveVariants && pendingSaveVariants.size > 0) ||
                        (() => {
                          let totalPrice;
                          // On cart page, use cartTotalFinalPrice from API if available
                          if (type === 'cart' && cartTotalFinalPrice > 0) {
                            const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                            totalPrice = cartTotalFinalPrice - discount;
                          } else if (type !== 'cart') {
                            // For address/payment, use navSummary first (from navigation state)
                            totalPrice = navSummary?.TOTAL_PRICE;
                            // If not available, calculate from navEntries (entriesSource should be navEntries on these pages)
                            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                                return sum + parseFloat(entry?.TOTAL_PRICE || 0);
                              }, 0);
                            }
                            // Final fallback to summarySource
                            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                              totalPrice = summarySource?.TOTAL_PRICE;
                            }
                          } else {
                            // For cart page, use summarySource first (live updates)
                            totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
                            // If not available, calculate from entries
                            if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                                return sum + parseFloat(entry?.TOTAL_PRICE || 0);
                              }, 0);
                            }
                          }

                          totalOrderAmountRef.current = totalPrice;
                          // return Math.round(totalPrice || 0) < PRICE_LOGIC.MIN_ORDER_PLACE_AMOUNT;
                          return false; // Always enable button
                        })() ||
                        // cart?.length === 0 ||
                        // !isAmountPresent ||
                        // showTotalsCalculating
                        false // Always enable button
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop view: Original layout */}
          <div className="hidden lg:block">
            {!isAmountZero && (
              <>
                {/* Total = cartTotalFinalPrice (already includes per-product LMD) */}
                <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                  <span>Total</span>
                  <span className="text-[#3DD0E5]">
                    {showTotalsCalculating
                      ? 'Calculating...'
                      : `₹${Math.round(cartTotalFinalPrice || 0)}`}
                  </span>
                </div>

                {/* Shipping Discount — shown when multiple products share LMD */}
                {(cartTotalLMD || 0) > (cartLMD || 0) && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-green-600">Shipping Discount</span>
                    <span className="text-green-600">
                      -₹{Math.round((cartTotalLMD || 0) - (cartLMD || 0))}
                    </span>
                  </div>
                )}

                {/* IM Points Applied Amount */}
                {getPaymentAdjustedTotal.hasIMPointsDiscount && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-green-600 font-medium">IM Points Applied:</span>
                    <span className="text-green-600 font-semibold">
                      -₹{Math.round(getPaymentAdjustedTotal.imPointsDiscount)}
                    </span>
                  </div>
                )}

                {/* IM Cash Applied Amount */}
                {getPaymentAdjustedTotal.hasIMCashDiscount && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-blue-600 font-medium">IM Cash Applied:</span>
                    <span className="text-blue-600 font-semibold">
                      -₹{Math.round(getPaymentAdjustedTotal.imCashDiscount)}
                    </span>
                  </div>
                )}

                {/* Grand Total */}
                <div className="flex justify-between font-bold text-base pt-1">
                  <span>
                    Grand Total <span className="text-xs font-medium text-gray-500">(incl. GST)</span>
                  </span>
                  <span className="text-[#3DD0E5]">
                    {showTotalsCalculating
                      ? 'Calculating...'
                      : (() => {
                        if (getPaymentAdjustedTotal.finalTotal !== null) {
                          return `₹${Math.round(getPaymentAdjustedTotal.finalTotal)}`;
                        }

                        let totalPrice;
                        if (type === 'cart' && cartTotalFinalPrice > 0) {
                          // Grand Total = cartTotalFinalPrice minus the shared-shipping discount
                          const discount = (cartTotalLMD || 0) > (cartLMD || 0)
                            ? (cartTotalLMD || 0) - (cartLMD || 0)
                            : 0;
                          totalPrice = cartTotalFinalPrice - discount;
                          } else if (type !== 'cart') {
                            if (cartTotalFinalPrice > 0) {
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0)
                                ? (cartTotalLMD || 0) - (cartLMD || 0)
                                : 0;
                              totalPrice = cartTotalFinalPrice - discount;
                            } else {
                              totalPrice = navSummary?.TOTAL_PRICE;
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                              if (totalPrice) totalPrice -= discount;
                            }
                            if (!totalPrice) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => sum + parseFloat(entry?.TOTAL_PRICE || 0), 0);
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                              if (totalPrice) totalPrice -= discount;
                            }
                            if (!totalPrice) {
                              totalPrice = summarySource?.TOTAL_PRICE;
                              const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                              if (totalPrice) totalPrice -= discount;
                            }
                          } else {
                            totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
                            const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                            if (totalPrice) totalPrice -= discount;
                            if (!totalPrice) {
                              totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => sum + parseFloat(entry?.TOTAL_PRICE || 0), 0);
                              if (totalPrice) totalPrice -= discount;
                            }
                          }
                        totalOrderAmountRef.current = totalPrice;
                        return `₹${Math.round(totalPrice || 0)}`;
                      })()}
                  </span>
                </div>
              </>
            )}

            {/* Shipping Charges */}
            {type !== 'cart' && totalLMDWithGST > 0 && (
              <div className="flex justify-between text-sm mt-2 pt-1">
                <span className="font-medium">Shipping:</span>
                <span className="text-[#3DD0E5] font-semibold">₹{Math.round(totalLMDWithGST)}</span>
              </div>
            )}

            {/* Removed Final Total Row to avoid double counting shipping */}

            {type !== 'payment' && (
              <div className="mt-4">
                <WebButton
                  className={`w-full bg-[#3DD0E5] text-white py-2.5 rounded-lg hover:bg-[#33BCCF] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                  text={type === 'address' ? 'Continue' : 'Place Order'}
                  onClick={type === 'address' ? handleProceedToPayment : handlePlaceOrder}
                  type="button"
                  disabled={
                    // !hasAllProductCardsMinMOQ ||
                    // !!invalidMOQItem ||
                    // (pendingSaveVariants && pendingSaveVariants.size > 0) ||
                    (() => {
                      let totalPrice;
                      // On cart page, use cartTotalFinalPrice from API if available
                      if (type === 'cart' && cartTotalFinalPrice > 0) {
                        // Grand Total = cartTotalFinalPrice minus shared-shipping discount
                        const discount = (cartTotalLMD || 0) > (cartLMD || 0) ? (cartTotalLMD || 0) - (cartLMD || 0) : 0;
                        totalPrice = cartTotalFinalPrice - discount;
                      } else if (type !== 'cart') {
                        // For address/payment, use navSummary first (from navigation state)
                        totalPrice = navSummary?.TOTAL_PRICE;
                        // If not available, calculate from navEntries (entriesSource should be navEntries on these pages)
                        if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                          totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                            return sum + parseFloat(entry?.TOTAL_PRICE || 0);
                          }, 0);
                        }
                        // Final fallback to summarySource
                        if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                          totalPrice = summarySource?.TOTAL_PRICE;
                        }
                      } else {
                        // For cart page, use summarySource first (live updates)
                        totalPrice = summarySource?.TOTAL_PRICE ?? navSummary?.TOTAL_PRICE;
                        // If not available, calculate from entries
                        if (totalPrice === undefined || totalPrice === null || totalPrice === 0) {
                          totalPrice = Object.values(entriesSource || {}).reduce((sum, entry) => {
                            return sum + parseFloat(entry?.TOTAL_PRICE || 0);
                          }, 0);
                        }
                      }

                      totalOrderAmountRef.current = totalPrice;
                      return Math.round(totalPrice || 0) < PRICE_LOGIC.MIN_ORDER_PLACE_AMOUNT;
                    })() ||
                    // cart?.length === 0
                    // !isAmountPresent
                    showTotalsCalculating
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
